/**
 * Checks and wording for the log-in, sign-up and reset forms (operator, 2026-09-23: "general basic standard practice
 * login and password reset flows"). The forms validate here, in words, instead of the browser's bubbles - an iPhone
 * says "match the requested format" and nobody knows what that means.
 */

/** New passwords only (sign-up, reset). Logging in never checks length, so older 6-character passwords still work. */
export const PASSWORD_MIN = 8;

export function normalizeEmail(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

// Deliberately loose: something@something.tld. The server is the real judge; this only catches typos early.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@.]{2,}$/;

export function isValidEmail(value) {
  return EMAIL_RE.test(normalizeEmail(value));
}

// The domains people mistype most, mapped to what they meant.
const DOMAIN_TYPOS = {
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'yahooo.com': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'icloud.co': 'icloud.com',
  'iclod.com': 'icloud.com',
  'icoud.com': 'icloud.com',
  'outlok.com': 'outlook.com',
  'outlook.co': 'outlook.com',
};

/** "you@gmial.com" -> "you@gmail.com"; null when the domain looks fine. */
export function suggestEmailFix(value) {
  const email = normalizeEmail(value);
  const at = email.lastIndexOf('@');
  if (at < 1) return null;
  const fix = DOMAIN_TYPOS[email.slice(at + 1)];
  return fix ? `${email.slice(0, at + 1)}${fix}` : null;
}

/** The rules a new password must meet, each with whether it does - drives the live checklist. */
export function passwordChecks(password) {
  const pw = String(password ?? '');
  return [
    { id: 'length', label: `${PASSWORD_MIN}+ characters`, ok: pw.length >= PASSWORD_MIN },
    { id: 'letter', label: 'A letter', ok: /\p{L}/u.test(pw) },
    { id: 'number', label: 'A number', ok: /\d/.test(pw) },
  ];
}

export function isPasswordAcceptable(password) {
  return passwordChecks(password).every((c) => c.ok);
}

const AUTH_ERROR_MESSAGES = {
  invalid_credentials: 'Wrong email or password.',
  user_already_exists: 'An account with this email already exists. Log in instead.',
  email_exists: 'An account with this email already exists. Log in instead.',
  weak_password: `Choose a stronger password - at least ${PASSWORD_MIN} characters with a letter and a number.`,
  same_password: 'That is your current password. Choose a new one.',
  email_address_invalid: 'That email address does not look right.',
  over_email_send_rate_limit: 'We just sent you an email. Wait a minute before asking for another.',
  over_request_rate_limit: 'Too many tries. Wait a minute and try again.',
  otp_expired: 'That code has expired. Request a new one.',
  user_not_found: 'No account uses that email.',
  signup_disabled: 'Sign-ups are closed right now.',
};

/** Supabase auth error -> a sentence a person can act on. */
export function friendlyAuthError(error, fallback = 'Something went wrong. Try again.') {
  if (!error) return fallback;
  const code = error.code || error.error_code;
  if (code && AUTH_ERROR_MESSAGES[code]) return AUTH_ERROR_MESSAGES[code];
  const message = String(error.message || '');
  // The app's own fetch timeout (supabaseFetch) - the phone had signal, it just could not carry the request in time.
  if (/timed out/i.test(message)) return 'Weak signal. Try again.';
  if (
    error.name === 'AuthRetryableFetchError' ||
    /failed to fetch|network|load failed/i.test(message)
  ) {
    return 'No connection. Try again when you have signal.';
  }
  if (/invalid login credentials/i.test(message)) return AUTH_ERROR_MESSAGES.invalid_credentials;
  if (/already registered/i.test(message)) return AUTH_ERROR_MESSAGES.user_already_exists;
  if (/rate limit|only request this after/i.test(message))
    return AUTH_ERROR_MESSAGES.over_request_rate_limit;
  return message.trim() || fallback;
}

/** Seconds Supabase makes you wait between two emails to the same address. */
export const EMAIL_RESEND_SECONDS = 60;
